import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useAuthStore } from '../../stores/authStore'
import { useAppStream } from '../../hooks/useAppStream'
import { useScrollRestoration } from '../../hooks/useScrollRestoration'
import { authService } from '../../services/authService'
import Sidebar from './Sidebar'
import Header from './Header'
import NotificationCenter from '../common/NotificationCenter'
import ChatDock from '../chat/ChatDock'
import ChatWidget from '../ai/ChatWidget'
import PartnerShell from '../partner/portal/PartnerShell'
import NotificationPanel, { NotificationBanners } from '../notifications/NotificationPanel'

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/sales/summary': 'Satış Özeti',
  '/sales/by-person': 'Satışçı Performans',
  '/sales/by-product': 'Ürün Analizi',
  '/sales/map': 'Satış Haritası',
  '/sales/customers': 'Satış Müşterileri',
  '/sales/orders': 'Siparişler',
  '/sales/deliveries': 'İrsaliyeler',
  '/sales/invoices': 'Faturalar',
  '/sales/targets': 'Satış Hedefleri',
  '/sales/approvals': 'Satış Onayları',
  '/stock/status': 'Stok Durumu',
  '/partner': 'İş Ortağı Paneli',
  '/partner/catalog': 'Ürün Kataloğu',
  '/partner/cart': 'Sepetim',
  '/partner/orders': 'Siparişlerim',
  '/partner/shipments': 'Sevkiyat Takibi',
  '/partner/invoices': 'Faturalarım',
  '/partner/stock': 'Stok Listem',
  '/partners/orders': 'İş Ortağı Siparişleri',
  '/stock/pending': 'Sevk Bekleyenler',
  '/stock/idle': 'Hareketsiz Stok',
  '/stock/movements': 'Ürün Hareketleri',
  '/stock/optimization': 'Stok Optimizasyon',
  '/stock/valuation': 'Stok Değerleme',
  '/finance/summary': 'Finans Özeti',
  '/finance/accounts': 'Cari Hesaplar',
  '/finance/aging': 'Yaşlandırma',
  '/finance/collections': 'Tahsilat',
  '/finance/invoices': 'Faturalar',
  '/finance/pnl': 'Kâr/Zarar',
  '/finance/bank-statements': 'Banka Ekstreleri',
  '/purchase/orders': 'Satınalma Siparişleri',
  '/purchase/approvals': 'Satınalma Onayları',
  '/purchase/suppliers': 'Tedarikçiler',
  '/purchase/reservations': 'Rezervasyonlar',
  '/automation/quote-approvals': 'Otomatik Teklif Onayı',
  '/quotes': 'Teklifler',
  '/settings/quote': 'Teklif Ayarları',
  '/settings/quote/templates': 'Teklif Şablonları',
  '/automation/my-documents': 'Belge Süreçlerim',
  '/crm/deals': 'Satış Fırsatları',
  '/crm/companies': 'CRM Şirketler',
  '/crm/contacts': 'CRM Kişiler',
  '/crm/customers': 'CRM Şirketler',
  '/crm/activities': 'Aktiviteler',
  '/crm/pipeline': 'Satış Hunisi',
  '/crm/reports': 'CRM Raporlar',
  '/analysis/profitability': 'Kârlılık Analizi',
  '/analysis/trends': 'Trendler',
  '/analysis/comparison': 'Dönem Karşılaştırma',
  '/settings/users': 'Kullanıcılar',
  '/settings/roles': 'Roller',
  '/settings/data/etl': 'ETL Yönetimi',
  '/settings/general': 'Ayarlar',
}

export default function Layout({ children }) {
  const { isAuthenticated, user } = useAuth()
  const location = useLocation()
  const isPartner = user?.role === 'PARTNER'
  const mainRef = useRef(null)
  useScrollRestoration(mainRef)

  // Birleşik SSE — TEK EventSource ile tüm realtime kanalları (quote/crm/tasks/mail).
  // Eski per-sayfa hook'ların yerini alır; HTTP/1.1'de bağlantı tükenmesini önler.
  useAppStream()

  // İzinler login anında dondurulur; yeni modül (ör. Sohbet) eklenince menü görünmezdi.
  // Açılışta sessizce tazele — kullanıcı yeniden giriş yapmak zorunda kalmasın.
  useEffect(() => {
    if (!isAuthenticated) return
    let alive = true
    authService.getMe()
      .then((res) => {
        const fresh = res?.permissions || res?.user?.permissions
        if (!alive || !fresh) return
        const cur = useAuthStore.getState().user
        if (cur && JSON.stringify(cur.permissions || {}) !== JSON.stringify(fresh)) {
          useAuthStore.getState().setUser({ ...cur, permissions: fresh })
        }
      })
      .catch(() => { /* sessiz — mevcut izinlerle devam */ })
    return () => { alive = false }
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return children
  }

  // İş ortağı: kendi portal kabuğu (tasarım teslimi vertex-partner-handoff 1.0) — iç
  // Sidebar/Header, sürüm etiketi, saat, AI ve yüzen sohbet düğmesi dış kullanıcıda yok.
  if (isPartner) {
    return (
      <>
        <PartnerShell>{children}</PartnerShell>
        <NotificationCenter />
      </>
    )
  }

  const pageTitle = PAGE_TITLES[location.pathname] || 'VERTEX'

  return (
    <div className="flex h-screen bg-surface-50 lg:p-2 lg:gap-2">
      <Sidebar />
      <div className="flex-1 flex min-w-0 overflow-hidden lg:rounded-2xl lg:border lg:border-gray-100 lg:shadow-sm bg-white">
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <Header pageTitle={pageTitle} />
          <main ref={mainRef} className="flex-1 overflow-y-auto overflow-x-hidden">
            {children}
          </main>
        </div>
        {/* Sohbet yan paneli — sayfa DEĞİŞSE DE açık kalır (kalıcı tercih).
            Akışın yanında durur, üstüne binmez: içerik daralır, kapanınca geri açılır. */}
        <ChatDock />
        {/* Bildirim paneli (macOS Bildirim Merkezi) — varsayılan açık, zilden aç/kapat; içerik daralır. */}
        <NotificationPanel />
      </div>
      <NotificationBanners />
      <NotificationCenter />
      <ChatWidget />
    </div>
  )
}
